import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Check } from '../CreateCheck/CheckCreationModal';
import { UserModel } from '../../UsersTable';
import Fetcher, { URLBuild } from '../../../services/fetcher';
import settings from '../../../settings';
import { ColDef, XGrid, useApiRef, PageChangeParams, ValueFormatterParams } from '@material-ui/x-grid';
import NumberFormat from 'react-number-format';
import { useNotification } from '../../../contexts/Notification';
import { Button } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: 15
  }
}));

function renderAmount(params: ValueFormatterParams): JSX.Element {
  return (
    <NumberFormat
      value={Number(params.value as string) / 100}
      thousandSeparator
      isNumericString
      suffix="€"
      displayType={'text'}
    />
  );
}

function dateFormatter(params: ValueFormatterParams): string {
  const date: Date = params.value as Date;
  return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function timeFormatter(params: ValueFormatterParams): string {
  const date: Date = params.value as Date;
  return date.toLocaleTimeString('fr-FR');
}

function renderBillDownloadButton(params: ValueFormatterParams): JSX.Element {
  const url = settings.PaymentProvider.paymentApi + '/check/pdf/' + (params.value as number);
  return (
    <Button
      style={{textTransform: 'none'}}
      onClick={() => {window.open(url as string)}}
      disabled={url === null}
    >
      <CloudDownloadIcon />
    </Button>
  );
}

const columns: ColDef[] = [
  {field: 'userId', headerName: 'ID Utilisateur', width: 200, sortable: false},
  {field: 'email', headerName: 'E-mail Utilisateur', width: 300, sortable: false},
  {field: 'amount', headerName: 'Montant', width: 200, sortable: false, renderCell: renderAmount, },
  {field: 'date', headerName: 'Date', width: 200, sortable: false, valueFormatter: dateFormatter},
  {field: 'time', headerName: 'Heure', width: 200, sortable: false, valueFormatter: timeFormatter},
  {field: 'bill', headerName: 'Facture', width: 100, sortable: false, renderCell: renderBillDownloadButton}
];

interface RowDef {
  id: string,
  userId: string,
  email: string,
  date: Date,
  time: Date,
  amount: string,
};

interface Props {
  checkAdded: Check | null,
  selectedUser: UserModel | null
}

const CheckGridContainer = (props: Props) => {
  const apiRef = useApiRef();
  const classes = useStyles();
  const limit = 15;
  const [rows, setRows] = useState<RowDef[]>([]);
  const [nbRows, setNbRows] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<any>({ limit });
  const [lastPages, setLastPages] = useState<any[]>([]);
  const notification = useNotification();

  useEffect(() => {
    if (!!props.checkAdded)
      getData();
  }, [props.checkAdded]);

  useEffect(() => {
    setParams((oldParams: any) => {
      const newParams = {...oldParams};
      if (!props.selectedUser && !!newParams.userId)
        delete newParams.userId;
      else if (props.selectedUser && props.selectedUser.id !== newParams.userId)
        newParams.userId = props.selectedUser.id;
      else
        return oldParams;
      return newParams;
    });
  }, [props.selectedUser]);
  
  useEffect(() => {
    getData();
  }, [params]);
  
  const getData = () => {
    setLoading(true);
    Fetcher.get(
      URLBuild(settings.PaymentProvider.paymentApi + '/check', params)
    ).then(data => {
      setNbRows(data.totalCount);
      setRows(data.data.map((row: any, i: number) => {
        const date = new Date(row.date);
        return {
          id: i,
          userId: row.userId,
          email: row.email,
          date: date,
          time: date,
          amount: row.amount,
          bill: row.billing_id
        };
      }));
      setLoading(false);
    }).catch((e: Error) => {
      notification.notify(e.message, "error");
      console.error(e);
      setLoading(false);
    });
  };

  const onPageChange = (param: PageChangeParams) => {
    if (param.rowCount === 0)
      return
    if (param.page === 1) {
      setParams((oldParam: any) => {
        const newParams = {...oldParam};
        if (newParams.lastUserId)
          delete newParams.lastUserId;
        if (newParams.lastDate)
          delete newParams.lastDate;
        return newParams;
      });
      setLastPages([]);
    } else {
      let exclusiveStart = {};
      if (lastPages.length === param.page - 2) {
        exclusiveStart = {lastUserId: rows[rows.length - 1].userId, lastDate: rows[rows.length - 1].date.toISOString()};
        const newLastPages = [...lastPages];
        newLastPages.push(exclusiveStart);
        setLastPages([...newLastPages]);
      } else {
        const newLastPages = [...lastPages];
        newLastPages.pop();
        exclusiveStart = newLastPages[newLastPages.length - 1];
        setLastPages(newLastPages);
      }
      setParams((oldParam: any) => ({...oldParam, ...exclusiveStart}));
    }
  };

  return (
    <div className={classes.container}>
      <XGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        pageSize={limit}
        paginationMode="server"
        pagination={true}
        loading={loading}
        hideFooterPagination={loading}
        rowCount={nbRows}
        hideFooterRowCount={true}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export default CheckGridContainer;